import { html, CSSResultGroup, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import componentStyles from './tufts-carousel-yext.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { ResizeController } from '../../controllers/resize-controller';
import Splide from '@splidejs/splide';
import {
  HealthcareProfessional,
  RatingResponse,
  verticalSearchResponseStructure,
  verticalSearchResults,
} from '../tufts-yext/tufts-yext-types';
import { ifDefined } from 'lit/directives/if-defined.js';

import {
  defaultImageTemplate,
  transformImageUrl,
  defaultImageTemplateSecond,
} from '../tufts-yext/image-template';
import { deleteDuplicateProfiles } from '../tufts-yext/tufts-yext-format';
import '../tufts-card-personnel/tufts-card-personnel';
import '../tufts-icon/tufts-icon';
import { classMap } from 'lit/directives/class-map.js';
import { TuftsContainer } from '../tufts-container/tufts-container';

/**
 * The Tufts carousel yext component
 *
 * @element Tufts-carousel-yext
 */
@customElement('tufts-carousel-yext')
export class TuftsCarousel extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  resizeController: ResizeController = new ResizeController(this);
  _splide: Splide;
  private _defaultTemplate: typeof defaultImageTemplate;
  isTintBackground = false;

  urlHref = 'https://cdn.yextapis.com/v2/accounts';
  accountId = 'me';
  contentType = 'search/vertical/query';
  apiKey = 'f70a1166dcbde8731713f74b5d21b86f';
  urlBase = `${this.urlHref}/${this.accountId}/${this.contentType}`;
  apiVersion = '20230406';
  apiVersionEntities = '20230301';
  experienceKey = 'tufts-fad';
  verticalKey = 'healthcare_professionals';
  version = 'PRODUCTION';
  locale = 'en';
  limitResultsYext = '50';
  // apiVersion = '20230301';

  @property({
    type: String,
    reflect: true,
    attribute: 'search',
  })
  search: string;
  @property({
    type: String,
    reflect: true,
    attribute: 'pairs',
  })
  pairs: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'view-all-link',
  })
  viewAllLink: string;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'randomization',
  })
  randomization: false;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'not-render-carousel',
  })
  notRenderCarousel: false;
  @property({
    type: String,
    reflect: true,
    attribute: 'heading-text',
  })
  headingText = 'Meet our clinicians';

  @property({
    type: String,
    reflect: true,
    attribute: 'button-text',
  })
  buttonText = 'See all clinicians at this location';

  @property({ type: Boolean, reflect: true, attribute: 'debug' })
  debug: null;

  @state()
  isMobile: boolean;

  @state()
  doctorResults: verticalSearchResults = [];

  @property({ type: Number, reflect: true, attribute: 'show-doctors' })
  showDoctors = 15;

  @property({ type: Number, reflect: true, attribute: 'specialty-number' })
  specialtyNumber = 5;

  @query('#splide1')
  _splideRef: HTMLElement;
  addingclass = '';

  fetchDoctorFromYext() {
    const params = new URLSearchParams();
    params.set('v', this.apiVersion);
    params.set('api_key', this.apiKey);
    params.set('experienceKey', this.experienceKey);
    params.set('verticalKey', this.verticalKey);
    params.set('version', this.version);
    params.set('limit', this.limitResultsYext);
    params.set('locale', this.locale);
    params.set('y_f_officeName', this.viewAllLink);
    const facetFiltersObject = {
      officeName: [
        {
          officeName: {
            $eq: this.viewAllLink,
          },
        },
      ],
    };

    const facetFiltersString = JSON.stringify(facetFiltersObject);

    const encodedFacetFilters = facetFiltersString.toString();

    params.set('facetFilters', encodedFacetFilters);

    const url = `${this.urlBase}?${params.toString()}`;

    fetch(url)
      .then(response => response.json())
      .then(
        (doctorResponse: {
          meta: {};
          response: verticalSearchResponseStructure;
        }) => {
          // Check if there are results in the doctorResults array.

          if (doctorResponse.response.results.length <= 0) {
            this.checkParentNodeAndUpdate();
            return;
          }

          // Process results and also check for container background color
          deleteDuplicateProfiles(doctorResponse);

          if (this.pairs) {
            this.handlePeerData(doctorResponse.response);
          }
          this.doctorResults = this.setRandomProfile(
            doctorResponse.response.results
          ).slice(0, this.showDoctors);

          // call rating after setrandomprofile
          this.fetchAdditionalProfileLocations(this.doctorResults);
          // Call checkParentNodeAndUpdate with hasResults=true
          this.checkParentNodeAndUpdate(true);
        }
      )
      .catch(error => {
        console.error(error);
        this.checkParentNodeAndUpdate();
      });
  }

  handlePeerData(response: verticalSearchResponseStructure) {
    // Parse the string representation of the "pairs" into an array of objects.
    const pairedEntities = JSON.parse(this.pairs) as {
      id: string;
      title: string;
    }[];

    response.entities.forEach(entity => {
      let matchingPairedEntity;
      if (entity.c_url_id) {
        // Find a matching object in "pairedEntities" based on the "id" property.
        matchingPairedEntity = pairedEntities.find(
          pairedEntity => pairedEntity.id === entity.c_url_id
        );
      }
      // Check if a match exists and ensure that "matchingPairedEntity.title" is not an empty string.
      if (matchingPairedEntity && matchingPairedEntity.title.length > 0) {
        // Create the "specialtyTitle" property of the entity with the "title" from the matched object.
        entity.specialtyTitle = [matchingPairedEntity.title];
      }
    });
  }

  setRandomProfile(results: verticalSearchResults) {
    if (results.length <= 1) {
      return results;
    }

    const selectedIndexes = new Set<number>();
    while (selectedIndexes.size < results.length) {
      const randomIndex = Math.floor(Math.random() * results.length);
      selectedIndexes.add(randomIndex);
    }

    const shuffledResults = Array.from(selectedIndexes).map(
      index => results[index]
    );
    results = shuffledResults;
    return results;
  }

  setFilterValue(
    filterObject: { $and: { string: string } },
    key: string,
    newValue: string[]
  ) {
    if (filterObject['$and'] instanceof Array) {
      const andArray = filterObject['$and'];
      for (const elementKey of andArray) {
        if (elementKey.hasOwnProperty(key)) {
          const existingKey = Object.keys(elementKey[key])[0];
          elementKey[key][existingKey] = newValue;
        }
      }
    }
    return filterObject;
  }

  /**
   * Checks the parent node for a background-color attribute
   * and updates it if there are no results
   *
   * @param hasResults Flag indicating if we have fetch results
   */

  checkParentNodeAndUpdate(hasResults = false) {
    // Look for the closest ancestor with the 'tufts-container' tag.
    const parentElement = this.closest('tufts-container') as Element;

    // If a 'tufts-container' ancestor is found
    if (parentElement) {
      if (!hasResults) {
        // If there are no results, set its 'background-color' attribute to 'white'.
        parentElement.setAttribute('background-color', 'white');
      } else {
        // If we have results, check if the container has a dark background color
        const bgColor = parentElement.getAttribute('background-color');
        const darkBackgrounds = [
          'green',
          'blue',
          'blue_dark',
          'purple',
          'purple_dark',
        ];

        if (bgColor && darkBackgrounds.includes(bgColor)) {
          // Add the dark-background class to the component for styling
          this.addingclass = 'dark-background';

          // When background is green, set pulse attributes
          if (bgColor === 'green') {
            // First set pulse-orientation to initialize the resizeController
            parentElement.setAttribute('pulse-orientation', 'bottom-left');

            // Give time for this update to be processed
            setTimeout(() => {
              // Then set the other attributes
              parentElement.setAttribute('max-pulse-size', '600px');
              parentElement.setAttribute('min-pulse-size', '25rem');
              parentElement.setAttribute('pulse-color', 'green');

              // Force an update of the container if possible
              if (parentElement instanceof HTMLElement) {
                const containerComp = parentElement as TuftsContainer;
                if (typeof containerComp.requestUpdate === 'function') {
                  containerComp.requestUpdate();
                }
              }
            }, 50); // Longer delay to ensure everything is processed correctly
          }
        }
      }
    }
  }

  firstUpdated() {
    if (this.notRenderCarousel) {
      this.checkParentNodeAndUpdate();
    } else {
      this.fetchDoctorFromYext();
    }
    // Determine which template to use once
    const parentContainer = this.closest('tufts-container');
    this.isTintBackground =
      parentContainer?.getAttribute('background-color') === 'tint';

    this._defaultTemplate = this.isTintBackground
      ? defaultImageTemplateSecond
      : defaultImageTemplate;
  }
  templateMobile() {
    return html`
      <div class="splide__track">
        <ul class="splide__list">
          ${this.doctorResults.map(doctor => {
            return html`
              <li class="splide__slide grid-one-result">
                ${this.tuftsCardPersonnelTemplate(doctor.data)}
              </li>
            `;
          })}
        </ul>
      </div>
      <div class="splide__arrows">
        <button
          class="splide__arrow splide__arrow--prev"
          aria-label="Previous"
        ></button>
        <button
          class="splide__arrow splide__arrow--next"
          aria-label="Next"
        ></button>
      </div>
    `;
  }
  divideArrayIntoGroups(array: verticalSearchResults, groupSize = 3) {
    const dividedArray = [];
    for (let i = 0; i < array.length; i += groupSize) {
      const group = array.slice(i, i + groupSize);
      dividedArray.push(group);
    }
    return dividedArray;
  }

  templateDesktop(onlyOneResult: boolean) {
    const doctorResultsDivider = this.divideArrayIntoGroups(this.doctorResults);

    return html`
      <div class="splide__arrows">
        <button
          class="splide__arrow splide__arrow--prev"
          aria-label="Previous"
        ></button>
        <button
          class="splide__arrow splide__arrow--next"
          aria-label="Next"
        ></button>
      </div>
      <div class="splide__track">
        <ul class="splide__list">
          ${doctorResultsDivider.map(doc => {
            return html`
              <li class="splide__slide">
                <div
                  class="grid-desktop ${onlyOneResult
                    ? 'grid-one-result'
                    : null}"
                >
                  ${doc.map(
                    doctor =>
                      html`${this.tuftsCardPersonnelTemplate(doctor.data)}`
                  )}
                </div>
              </li>
            `;
          })}
        </ul>
      </div>
    `;
  }

  createReferenceSplide() {
    if (this._splide) {
      this._splide.destroy(true);
    }

    if (this._splideRef) {
      if (this.isMobile) {
        this._splide = new Splide(this._splideRef, {
          speed: 300,
          pagination: false,
        }).mount();
      } else {
        this._splide = new Splide(this._splideRef, {
          speed: 300,
        }).mount();
      }
    }
  }

  tuftsCardPersonnelTemplate(doctor: HealthcareProfessional): TemplateResult {
    const profilePicture = doctor.headshot?.url;

    let profileDetailsUrl = '';

    if (doctor.c_url_id) {
      if (this.debug) {
        profileDetailsUrl = `/?path=/story/content-yext-yext-item--tufts-yext-item&yext=${doctor.id}`;
      } else {
        profileDetailsUrl = `/doctor/${doctor.c_url_id}`;
      }
    }
    const doctorSpecialty = doctor.c_specialtyName;
    return html`
      <tufts-card-personnel
        vertical-trigger-width="300"
        version="card-rating ${this.isTintBackground ? 'tint' : null}"
      >
        <div slot="photo">
          ${profilePicture
            ? html`<img
                alt="${ifDefined(doctor.name)}"
                src="${transformImageUrl(profilePicture, 698, 465)}"
              />`
            : this._defaultTemplate()}
        </div>
        <div slot="name">
          <tufts-heading
            text-color="heading-color"
            clickable-card
            level-size="md"
          >
            <h4>
              <a
                href="${profileDetailsUrl}"
                class="btn small link arrow-right"
                button-label="View ${doctor.name}'s Profile"
              >
                ${doctor.name}
              </a>
            </h4>
          </tufts-heading>
        </div>
        ${doctorSpecialty && doctorSpecialty.length !== 0
          ? html` <div slot="credentials">${doctorSpecialty.join(', ')}</div> `
          : null}
        ${doctor.ratings
          ? html`<div slot="phone">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.5 17.6015L8.37983 20.0836C8.19781 20.1994 8.00752 20.249 7.80896 20.2325C7.6104 20.2159 7.43666 20.1497 7.28774 20.0339C7.13881 19.9181 7.02299 19.7733 6.94025 19.5996C6.85752 19.4258 6.84097 19.2314 6.89061 19.0163L7.9827 14.3252L4.33412 11.1731C4.16865 11.0241 4.06523 10.8545 4.02387 10.6643C3.9825 10.474 3.99491 10.2878 4.0611 10.1058C4.12728 9.92378 4.22656 9.77486 4.35894 9.65903C4.49131 9.5432 4.67333 9.46874 4.90499 9.43565L9.72013 9.0137L11.5816 4.59569C11.6644 4.39712 11.7926 4.2482 11.9664 4.14892C12.1401 4.04964 12.318 4 12.5 4C12.682 4 12.8599 4.04964 13.0336 4.14892C13.2074 4.2482 13.3356 4.39712 13.4184 4.59569L15.2799 9.0137L20.095 9.43565C20.3267 9.46874 20.5087 9.5432 20.6411 9.65903C20.7734 9.77486 20.8727 9.92378 20.9389 10.1058C21.0051 10.2878 21.0175 10.474 20.9761 10.6643C20.9348 10.8545 20.8314 11.0241 20.6659 11.1731L17.0173 14.3252L18.1094 19.0163C18.159 19.2314 18.1425 19.4258 18.0597 19.5996C17.977 19.7733 17.8612 19.9181 17.7123 20.0339C17.5633 20.1497 17.3896 20.2159 17.191 20.2325C16.9925 20.249 16.8022 20.1994 16.6202 20.0836L12.5 17.6015Z"
                  fill="#00308C"
                />
              </svg>
              <span
                ><strong>${doctor.ratings}</strong> (${doctor.comment_count}
                reviews)</span
              >
            </div>`
          : null}
        ${doctor.acceptingNewPatients
          ? html`<div slot="phone">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M10.6016 16.5999L17.6516 9.5499L16.2516 8.1499L10.6016 13.7999L7.75156 10.9499L6.35156 12.3499L10.6016 16.5999Z"
                  fill="#428FEC"
                />
                <path
                  d="M21.21 8.1C20.69 6.88 19.97 5.83 19.07 4.93C18.17 4.03 17.11 3.32 15.9 2.79C14.68 2.27 13.38 2 12 2C10.62 2 9.32 2.26 8.1 2.79C6.88 3.32 5.83 4.03 4.93 4.93C4.03 5.83 3.32 6.89 2.79 8.1C2.26 9.32 2 10.62 2 12C2 13.38 2.26 14.68 2.79 15.9C3.31 17.12 4.03 18.18 4.93 19.07C5.83 19.96 6.89 20.68 8.1 21.21C9.32 21.74 10.62 22 12 22C13.38 22 14.68 21.74 15.9 21.21C17.12 20.69 18.18 19.97 19.07 19.07C19.97 18.17 20.68 17.11 21.21 15.9C21.74 14.68 22 13.38 22 12C22 10.62 21.74 9.32 21.21 8.1ZM17.67 17.68C16.12 19.23 14.23 20.01 11.99 20.01C9.75 20.01 7.87 19.23 6.32 17.68C4.77 16.13 3.99 14.24 3.99 12.01C3.99 9.78 4.77 7.89 6.32 6.34C7.87 4.79 9.76 4.01 11.99 4.01C14.22 4.01 16.11 4.79 17.67 6.34C19.22 7.89 19.99 9.78 19.99 12.01C19.99 14.24 19.22 16.13 17.67 17.68Z"
                  fill="#00308C"
                />
              </svg>
              Accepting new patients
            </div>`
          : null}
        ${doctor.c_openScheduling
          ? html`
              <div slot="view-profile">
                <a
                  class="btn small reversed compact open-scheduling"
                  href="/doctor/${doctor.c_url_id}?open-scheduling"
                  >Schedule online</a
                >
              </div>
            `
          : html`
              <div slot="view-profile">
                <a
                  class="btn small link arrow-right"
                  button-label="View Profile"
                >
                  View Profile
                </a>
              </div>
            `}
      </tufts-card-personnel>
    `;
  }

  // Function to generate search URL
  getFilterValue(objeto: { $and: { string: string } }, keyParams: string) {
    if (objeto['$and'] instanceof Array) {
      const andArray = objeto['$and'];
      for (const elementKey of andArray) {
        if (elementKey.hasOwnProperty(keyParams)) {
          const clave = Object.keys(elementKey[keyParams])[0];
          const value = elementKey[keyParams][clave];
          return value;
        }
      }
    }
    return null; // Value null if the key not exist
  }

  getSearchParameterFullAddress(searchParams: {
    $and: { string: string };
  }): string {
    const addressParts = [
      this.getFilterValue(searchParams, 'address.line1'),
      this.getFilterValue(searchParams, 'address.line2'),
      this.getFilterValue(searchParams, 'address.city'),
      this.getFilterValue(searchParams, 'address.region'),
      this.getFilterValue(searchParams, 'address.postalCode'),
    ].filter(Boolean);

    return addressParts.join(' ');
  }

  /**
   * Generates the view all button template with appropriate URL parameters
   * @returns {TemplateResult|null} The button template or null if viewAllLink is not set
   */
  viewAllButtonTemplate() {
    if (!this.viewAllLink) {
      return null;
    }
    const params = new URLSearchParams();
    params.set('y_f_officeName', this.viewAllLink);

    const profileDetailsUrl = this.debug
      ? `/iframe.html?id=content-yext-yext-list--tufts-yext&args=&viewMode=story&${params.toString()}`
      : `/find-a-doctor?${params.toString()}`;

    return html`
      <div class="container-button-doctors">
        <tufts-button
          button-variant="link"
          button-url=${profileDetailsUrl}
          button-label="${this.buttonText}"
        >
          ${this.buttonText}
        </tufts-button>
      </div>
    `;
  }

  async fetchAdditionalProfileLocations(searchResults: verticalSearchResults) {
    // Create an array of promises for each result
    const promises = searchResults.map(async searchResult => {
      if (searchResult) {
        try {
          // Construct the URL for the rating API
          const ratingResponse = await fetch(
            `https://ratings.md/api/v1/widget/profiles/${searchResult.data.npi}?account=tufts-medicine&id_type=npi`
          );

          // Parse the JSON response into a RatingResponse object
          const ratingData: RatingResponse = await ratingResponse.json();

          // If the ratingData contains a message, it indicates an error, so return early
          if (ratingData.message) {
            return;
          } else {
            searchResult.data.ratings = ratingData.rating;
            searchResult.data.comment_count = ratingData.comment_count;
          }
        } catch (error) {
          // Error is intentionally unhandled as there's no user-facing action
          // or recovery to be done if this service fails.
        }
      }
    });

    // Wait for all promises to resolve
    await Promise.all(promises);
    // Request an update to the component to reflect the new data
    this.requestUpdate();
  }

  render() {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;

    setTimeout(() => {
      this.createReferenceSplide();
    }, 0);
    const onlyOneResult: boolean = this.doctorResults.length <= 1;
    const wrapperClass = {
      splide: true,
      isMobile: this.isMobile,
      [this.addingclass]: this.addingclass,
    };
    return this.doctorResults.length !== 0
      ? html`
          <div class="heading-with-link">
            <div class="icon-heading-wrapper">
              <tufts-icon
                library="system"
                name="doctor"
                size="4rem"
              ></tufts-icon>
              <tufts-heading level-size="xl" class="block">
                ${this.headingText}
              </tufts-heading>
            </div>
            ${this.viewAllButtonTemplate()}
          </div>
          <div class=${classMap(wrapperClass)} id="splide1">
            ${this.isMobile
              ? html`${this.templateMobile()}`
              : html`${this.templateDesktop(onlyOneResult)}`}
          </div>
        `
      : null;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-carousel-yext': TuftsCarousel;
  }
}
